import { createClient } from '@supabase/supabase-js';
import { logger } from './logger';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  logger.warn('Supabase credentials not configured. Running in offline mode.');
}

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        storageKey: 'ja-schools-guide-auth',
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    })
  : null;

// Helper to check if Supabase is configured
export const isSupabaseConfigured = () => !!supabase;

// ==================== AUTH HELPERS ====================
export const auth = {
  signUp: async (email, password, metadata = {}) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata }
    });
  },

  signIn: async (email, password) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.auth.signInWithPassword({ email, password });
  },

  signOut: async () => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.auth.signOut();
  },

  getUser: async () => {
    if (!supabase) return { data: { user: null } };
    return await supabase.auth.getUser();
  },

  getSession: async () => {
    if (!supabase) return { data: { session: null } };
    return await supabase.auth.getSession();
  },

  onAuthStateChange: (callback) => {
    if (!supabase) return { data: { subscription: { unsubscribe: () => {} } } };
    return supabase.auth.onAuthStateChange(callback);
  }
};

// ==================== DATABASE HELPERS ====================
export const db = {
  // Service Providers - UNIFIED TABLE for ALL education service provider types
  // Types: tutor, math_tutor, science_tutor, language_tutor, test_prep, consultant, school_supplies, uniforms, private_school, other
  serviceProviders: {
    getAll: async (type = null) => {
      if (!supabase) return { data: [], error: null };
      try {
        let query = supabase.from('service_providers').select('*').eq('is_active', true).order('name');
        if (type) query = query.eq('type', type);
        const result = await query;
        if (result.error) return { data: [], error: null };
        return result;
      } catch {
        return { data: [], error: null };
      }
    },
    // Admin version - returns ALL providers including inactive
    getAllAdmin: async (type = null) => {
      if (!supabase) return { data: [], error: null };
      try {
        let query = supabase.from('service_providers').select('*').order('created_at', { ascending: false });
        if (type) query = query.eq('type', type);
        const result = await query;
        if (result.error) return { data: [], error: null };
        return result;
      } catch {
        return { data: [], error: null };
      }
    },
    getByType: async (type) => {
      if (!supabase) return { data: [], error: null };
      try {
        const result = await supabase.from('service_providers').select('*').eq('type', type).eq('is_active', true).order('name');
        if (result.error) return { data: [], error: null };
        return result;
      } catch {
        return { data: [], error: null };
      }
    },
    getByTypeAdmin: async (type) => {
      if (!supabase) return { data: [], error: null };
      try {
        const result = await supabase.from('service_providers').select('*').eq('type', type).order('created_at', { ascending: false });
        if (result.error) return { data: [], error: null };
        return result;
      } catch {
        return { data: [], error: null };
      }
    },
    getById: async (id) => {
      if (!supabase) return { data: null, error: null };
      try {
        const result = await supabase.from('service_providers').select('*').eq('id', id).single();
        if (result.error) return { data: null, error: null };
        return result;
      } catch {
        return { data: null, error: null };
      }
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      try {
        return await supabase.from('service_providers').insert(data).select().single();
      } catch {
        return { data: null, error: { message: 'Unable to create service provider' } };
      }
    },
    update: async (id, data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      try {
        return await supabase.from('service_providers').update(data).eq('id', id).select().single();
      } catch {
        return { data: null, error: { message: 'Unable to update service provider' } };
      }
    },
    delete: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      try {
        return await supabase.from('service_providers').delete().eq('id', id);
      } catch {
        return { error: { message: 'Unable to delete service provider' } };
      }
    },
    // Find all service providers linked to a user (by user_id)
    // All education service provider types are stored in the service_providers table
    getByOwnerId: async (userId) => {
      if (!supabase) return { data: [], error: null };
      try {
        const { data, error } = await supabase
          .from('service_providers')
          .select('id, name, type, tier, is_active, logo_url, email, phone, parish')
          .eq('user_id', userId);

        if (error) return { data: [], error: null };

        // Add providerTable for compatibility with VendorPortal
        const providers = (data || []).map(p => ({ ...p, providerTable: 'service_providers' }));
        return { data: providers, error: null };
      } catch {
        return { data: [], error: null };
      }
    }
  },

  // Service Provider Reviews
  reviews: {
    getByProvider: async (providerId) => {
      if (!supabase) return { data: [], error: null };
      return supabase
        .from('service_provider_reviews')
        .select('*')
        .eq('provider_id', providerId)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });
    },
    getSummaryForProviders: async (providerIds) => {
      if (!supabase) return { data: [], error: null };
      return supabase
        .from('service_provider_reviews')
        .select('provider_id, rating')
        .in('provider_id', providerIds)
        .eq('status', 'approved');
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return supabase.from('service_provider_reviews').insert([data]).select().single();
    },
    getAll: async (status = null) => {
      if (!supabase) return { data: [], error: null };
      let query = supabase
        .from('service_provider_reviews')
        .select('*, service_providers(name, type)')
        .order('created_at', { ascending: false });
      if (status) query = query.eq('status', status);
      return query;
    },
    updateStatus: async (id, status, adminNotes = null) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return supabase
        .from('service_provider_reviews')
        .update({ status, admin_notes: adminNotes, reviewed_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
    },
  },

  // Business Submissions (Get Listed)
  submissions: {
    getAll: async (status = null) => {
      if (!supabase) return { data: [], error: null };
      let query = supabase.from('business_submissions').select('*').order('created_at', { ascending: false });
      if (status) query = query.eq('status', status);
      return await query;
    },
    getById: async (id) => {
      if (!supabase) return { data: null, error: null };
      return await supabase.from('business_submissions').select('*').eq('id', id).single();
    },
    create: async (data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      // Don't use .select() - regular users only have INSERT permission, not SELECT
      return await supabase.from('business_submissions').insert({ ...data, status: 'pending' });
    },
    approve: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').update({ status: 'approved', reviewed_at: new Date().toISOString() }).eq('id', id).select().single();
    },
    reject: async (id, reason = '') => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').update({ status: 'rejected', rejection_reason: reason, reviewed_at: new Date().toISOString() }).eq('id', id).select().single();
    },
    delete: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').delete().eq('id', id);
    },
    resetToPending: async (id) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').update({ status: 'pending', reviewed_at: null }).eq('id', id).select().single();
    },
    verifyPayment: async (id, verifiedBy = null) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('business_submissions').update({
        payment_verified: true,
        payment_verified_by: verifiedBy,
        payment_verified_at: new Date().toISOString()
      }).eq('id', id).select().single();
    }
  },

  // User Profiles
  profiles: {
    get: async (userId) => {
      if (!supabase) return { data: null, error: null };
      return await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
    },
    update: async (userId, data) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('profiles').update(data).eq('id', userId).select().single();
    },
    getAll: async () => {
      if (!supabase) return { data: [], error: null };
      return await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    },
    setRole: async (userId, role) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('profiles').update({ role }).eq('id', userId).select().single();
    },
    suspend: async (userId, suspended = true) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      return await supabase.from('profiles').update({ is_suspended: suspended }).eq('id', userId).select().single();
    },
    delete: async (userId) => {
      if (!supabase) return { error: { message: 'Supabase not configured' } };
      // Delete profile - auth user must be deleted separately via Supabase dashboard
      return await supabase.from('profiles').delete().eq('id', userId);
    }
  },

  // Analytics / Stats
  stats: {
    getDashboard: async () => {
      if (!supabase) return { data: null, error: null };
      try {
        // Query only school-related tables that exist
        const safeCount = async (table, filter = null) => {
          try {
            let query = supabase.from(table).select('id', { count: 'exact', head: true });
            if (filter) query = query.eq(filter.column, filter.value);
            const result = await query;
            return result.count || 0;
          } catch {
            return 0;
          }
        };

        const [users, serviceProviders, submissions, schools] = await Promise.all([
          safeCount('profiles'),
          safeCount('service_providers'),
          safeCount('business_submissions', { column: 'status', value: 'pending' }),
          safeCount('schools')
        ]);

        return {
          data: {
            totalUsers: users,
            totalServiceProviders: serviceProviders,
            pendingSubmissions: submissions,
            totalSchools: schools
          },
          error: null
        };
      } catch {
        return { data: { totalUsers: 0, totalServiceProviders: 0, pendingSubmissions: 0, totalSchools: 0 }, error: null };
      }
    }
  }
};

// ==================== STORAGE HELPERS ====================
export const storage = {
  uploadImage: async (bucket, path, file) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: '3600',
      upsert: false
    });
  },
  getPublicUrl: (bucket, path) => {
    if (!supabase) return null;
    return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
  },
  deleteImage: async (bucket, path) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.storage.from(bucket).remove([path]);
  }
};

// ==================== BANNER ADS ====================
export const bannerAds = {
  getActive: async (page = null) => {
    if (!supabase) return { data: [], error: null };
    try {
      let query = supabase
        .from('banner_ads')
        .select('*')
        .eq('is_active', true)
        .or('start_date.is.null,start_date.lte.' + new Date().toISOString().split('T')[0])
        .or('end_date.is.null,end_date.gte.' + new Date().toISOString().split('T')[0])
        .order('priority', { ascending: false });

      if (page) {
        query = query.contains('pages', [page]);
      }
      const result = await query;
      if (result.error) return { data: [], error: null }; // Table may not exist
      return result;
    } catch {
      return { data: [], error: null };
    }
  },
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    try {
      const result = await supabase.from('banner_ads').select('*').order('created_at', { ascending: false });
      if (result.error) return { data: [], error: null };
      return result;
    } catch {
      return { data: [], error: null };
    }
  },
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    try {
      const result = await supabase.from('banner_ads').select('*').eq('id', id).maybeSingle();
      if (result.error) return { data: null, error: null };
      return result;
    } catch {
      return { data: null, error: null };
    }
  },
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ads').insert(data).select().single();
  },
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ads').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id).select().single();
  },
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ads').delete().eq('id', id);
  },
  getByAdvertiser: async (advertiserId) => {
    if (!supabase) return { data: [], error: null };
    try {
      const result = await supabase.from('banner_ads').select('*').eq('advertiser_id', advertiserId).order('created_at', { ascending: false });
      if (result.error) return { data: [], error: null };
      return result;
    } catch {
      return { data: [], error: null };
    }
  }
};

// ==================== LISTING TIERS ====================
export const listingTiers = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase.from('listing_tiers').select('*').eq('is_active', true).order('sort_order');
  },
  getByName: async (name) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('listing_tiers').select('*').eq('name', name).single();
  }
};

// ==================== APP SETTINGS ====================
export const appSettings = {
  get: async (key) => {
    if (!supabase) return { data: null, error: null };
    try {
      const { data, error } = await supabase.from('app_settings').select('value').eq('key', key).maybeSingle();
      if (error) return { data: null, error: null }; // Return null if setting/table doesn't exist
      return { data: data?.value, error: null };
    } catch {
      return { data: null, error: null };
    }
  },
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    try {
      const result = await supabase.from('app_settings').select('*').order('key');
      if (result.error) return { data: [], error: null }; // Return empty if table doesn't exist
      return result;
    } catch {
      return { data: [], error: null };
    }
  },
  set: async (key, value, description = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('app_settings').upsert({
      key,
      value,
      description,
      updated_at: new Date().toISOString()
    }, { onConflict: 'key' });
  }
};

// ==================== DATA CORRECTIONS ====================
export const dataCorrections = {
  submit: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    try {
      const result = await supabase.from('data_corrections').insert(data).select().single();
      if (result.error) return { data: null, error: { message: 'Unable to submit correction. Please try again later.' } };

      // Notify admins — 'data_correction' is in the original type constraint so this always works
      const notif = {
        type: 'data_correction',
        title: `Data Correction: ${data.category || 'General'}`,
        message: `${data.reporter_name || data.reporter_email} submitted a correction`,
        data: {
          submission_id: result.data?.id,
          category: data.category,
          section: data.section,
          current_content: data.current_content,
          correct_content: data.correct_content,
          reporter_name: data.reporter_name,
          reporter_email: data.reporter_email,
          reporter_phone: data.reporter_phone,
        }
      };
      adminNotifications.create(notif).catch(() => {});
      adminNotifications.sendEmail(notif).catch(() => {});

      return result;
    } catch {
      return { data: null, error: { message: 'Unable to submit correction. Please try again later.' } };
    }
  },
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    try {
      let query = supabase.from('data_corrections').select('*').order('created_at', { ascending: false });
      if (status) query = query.eq('status', status);
      const result = await query;
      if (result.error) return { data: [], error: null };
      return result;
    } catch {
      return { data: [], error: null };
    }
  },
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    try {
      const result = await supabase.from('data_corrections').select('*').eq('id', id).maybeSingle();
      if (result.error) return { data: null, error: null };
      return result;
    } catch {
      return { data: null, error: null };
    }
  },
  updateStatus: async (id, status, adminNotes = null, reviewerId = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const updates = {
      status,
      admin_notes: adminNotes,
      reviewed_by: reviewerId,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    if (status === 'fixed') {
      updates.fixed_at = new Date().toISOString();
    }
    return await supabase.from('data_corrections').update(updates).eq('id', id).select().single();
  },
  getStats: async () => {
    if (!supabase) return { data: null, error: null };
    const [pending, reviewing, verified, fixed, rejected] = await Promise.all([
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'reviewing'),
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'verified'),
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'fixed'),
      supabase.from('data_corrections').select('id', { count: 'exact', head: true }).eq('status', 'rejected')
    ]);
    return {
      data: {
        pending: pending.count || 0,
        reviewing: reviewing.count || 0,
        verified: verified.count || 0,
        fixed: fixed.count || 0,
        rejected: rejected.count || 0,
        total: (pending.count || 0) + (reviewing.count || 0) + (verified.count || 0) + (fixed.count || 0) + (rejected.count || 0)
      },
      error: null
    };
  }
};

// ==================== ANALYTICS HELPERS ====================
// Simplified - custom analytics removed, use GA4 instead
// These return empty data for backwards compatibility with AdminAnalytics
export const analyticsDb = {
  // Real localized figures from Supabase counters. Page-level traffic (page views,
  // sessions, devices, referrers, search terms) now lives in Google Analytics 4.
  getSummary: async () => {
    if (!supabase) return { data: [{}], error: null };
    try {
      const [sp, ads] = await Promise.all([
        supabase.from('service_providers').select('view_count, click_count'),
        supabase.from('banner_ads').select('impressions, clicks')
      ]);
      const providers = sp.data || [];
      const banners = ads.data || [];
      const directory_views = providers.reduce((s, p) => s + (p.view_count || 0), 0);
      const directory_clicks = providers.reduce((s, p) => s + (p.click_count || 0), 0);
      const ad_impressions = banners.reduce((s, a) => s + (a.impressions || 0), 0);
      const ad_clicks = banners.reduce((s, a) => s + (a.clicks || 0), 0);
      return { data: [{
        directory_views,
        directory_clicks,
        ad_impressions,
        ad_clicks,
        total_events: directory_views + directory_clicks + ad_impressions + ad_clicks,
        page_views: 0,       // tracked in Google Analytics 4
        unique_sessions: 0   // tracked in Google Analytics 4
      }], error: null };
    } catch {
      return { data: [{}], error: null };
    }
  },
  getTopEntities: async (start, end, entityType = 'school', limit = 10) => {
    if (!supabase) return { data: [], error: null };
    try {
      // Real localized signal: most-voted schools (community "Hot Schools" votes)
      const { data } = await supabase.from('school_votes').select('school_id, school_name');
      const counts = {};
      (data || []).forEach(v => {
        if (!counts[v.school_id]) counts[v.school_id] = { entity_id: v.school_id, entity_name: v.school_name, view_count: 0 };
        counts[v.school_id].view_count++;
      });
      const result = Object.values(counts).sort((a, b) => b.view_count - a.view_count).slice(0, limit);
      return { data: result, error: null };
    } catch {
      return { data: [], error: null };
    }
  },
  getByParish: async () => ({ data: [], error: null }),
  getTopPages: async () => ({ data: [], error: null }),
  getTopSearches: async () => ({ data: [], error: null }),
  getDeviceBreakdown: async () => ({ data: [], error: null }),
  getReferrers: async () => ({ data: [], error: null }),
  getDailyTrend: async () => ({ data: [], error: null }),
  getRecentEvents: async () => ({ data: [], error: null }),
  getTopDirectoryListings: async (start, end, limit = 15) => {
    if (!supabase) return { data: [], error: null };
    try {
      const { data } = await supabase
        .from('service_providers')
        .select('id, name, type, parish, view_count, click_count')
        .order('view_count', { ascending: false })
        .limit(limit);
      return { data: (data || []).map(p => ({ ...p, view_count: p.view_count || 0, click_count: p.click_count || 0 })), error: null };
    } catch {
      return { data: [], error: null };
    }
  },
  getListingViewsByType: async () => {
    if (!supabase) return { data: [], error: null };
    try {
      const { data } = await supabase.from('service_providers').select('type, view_count');
      const byType = {};
      (data || []).forEach(p => {
        const t = p.type || 'other';
        byType[t] = (byType[t] || 0) + (p.view_count || 0);
      });
      const result = Object.entries(byType)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count);
      return { data: result, error: null };
    } catch {
      return { data: [], error: null };
    }
  },
  // Keep ad performance - queries banner_ads table
  getAdPerformance: async () => {
    if (!supabase) return { data: [], error: null };
    try {
      const { data, error } = await supabase
        .from('banner_ads')
        .select('id, name, advertiser_name, placement, impressions, clicks, ctr, is_active')
        .order('impressions', { ascending: false });
      return { data: data || [], error };
    } catch {
      return { data: [], error: null };
    }
  }
};

// ==================== PAYMENT PROOFS ====================
export const paymentProofs = {
  getBySubmission: async (submissionId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('payment_proofs')
      .select('*')
      .eq('submission_id', submissionId)
      .order('created_at', { ascending: false });
  },
  getByUser: async (userId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('payment_proofs')
      .select('*, business_submissions(business_name, tier, status)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
  },
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('payment_proofs')
      .insert(data)
      .select()
      .single();
  },
  updateStatus: async (id, status, reviewerId = null, rejectionReason = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const updates = {
      status,
      reviewed_by: reviewerId,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    if (rejectionReason) updates.rejection_reason = rejectionReason;
    return await supabase
      .from('payment_proofs')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
  },
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('payment_proofs')
      .select('*, business_submissions(business_name, tier, contact_email)')
      .order('created_at', { ascending: false });
    if (status) query = query.eq('status', status);
    return await query;
  }
};

// ==================== USER NOTIFICATION PREFERENCES ====================
export const notificationPreferences = {
  get: async (userId) => {
    if (!supabase) return { data: null, error: null };
    try {
      const result = await supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (result.error) return { data: null, error: null }; // Table may not exist
      return result;
    } catch {
      return { data: null, error: null };
    }
  },
  update: async (userId, preferences) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('user_notification_preferences')
      .upsert({
        user_id: userId,
        ...preferences,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
  }
};

// ==================== USER SUBMISSIONS (for dashboard) ====================
export const userSubmissions = {
  getByUser: async (userId) => {
    if (!supabase) return { data: [], error: null };
    try {
      const result = await supabase
        .from('business_submissions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (result.error) return { data: [], error: null }; // Table may not exist
      return result;
    } catch {
      return { data: [], error: null };
    }
  },
  getByEmail: async (email) => {
    if (!supabase) return { data: [], error: null };
    try {
      const result = await supabase
        .from('business_submissions')
        .select('*')
        .eq('contact_email', email)
        .order('created_at', { ascending: false });
      if (result.error) return { data: [], error: null };
      return result;
    } catch {
      return { data: [], error: null };
    }
  },
  linkToUser: async (submissionId, userId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('business_submissions')
      .update({ user_id: userId })
      .eq('id', submissionId)
      .select()
      .single();
  }
};

// ==================== ADMIN NOTIFICATIONS ====================
export const adminNotifications = {
  // Get all notifications (with optional filters)
  getAll: async (filters = {}) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('admin_notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters.type) query = query.eq('type', filters.type);
    if (filters.is_read !== undefined) query = query.eq('is_read', filters.is_read);
    if (filters.limit) query = query.limit(filters.limit);

    return await query;
  },

  // Get unread count
  getUnreadCount: async () => {
    if (!supabase) return { count: 0, error: null };
    const { count, error } = await supabase
      .from('admin_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('is_read', false);
    return { count: count || 0, error };
  },

  // Mark notification as read
  markAsRead: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('admin_notifications')
      .update({ is_read: true })
      .eq('id', id);
  },

  // Mark all as read
  markAllAsRead: async () => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('admin_notifications')
      .update({ is_read: true })
      .eq('is_read', false);
  },

  // Delete notification
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('admin_notifications')
      .delete()
      .eq('id', id);
  },

  // Create a new notification
  create: async ({ type, title, message, data = {} }) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('admin_notifications')
      .insert({
        type,
        title,
        message,
        data,
        is_read: false,
        created_at: new Date().toISOString()
      })
      .select()
      .single();
  },

  // Send email notification via Edge Function
  sendEmail: async (notification) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    try {
      const { data, error } = await supabase.functions.invoke('send-admin-notification', {
        body: notification
      });
      return { data, error };
    } catch (error) {
      logger.error('Error sending notification email:', error);
      return { data: null, error };
    }
  },

  // Subscribe to new notifications (real-time)
  subscribe: (callback) => {
    if (!supabase) return { unsubscribe: () => {} };
    const channel = supabase
      .channel('admin-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_notifications'
        },
        (payload) => {
          callback(payload.new);
          // Auto-trigger email for new notifications
          adminNotifications.sendEmail({
            notification_id: payload.new.id,
            type: payload.new.type,
            title: payload.new.title,
            message: payload.new.message,
            data: payload.new.data
          });
        }
      )
      .subscribe();

    return {
      unsubscribe: () => supabase.removeChannel(channel)
    };
  }
};

// ==================== SUBSCRIPTIONS ====================
export const subscriptions = {
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('vendor_subscriptions')
      .select(`
        *,
        vendor:service_providers(id, name, email, phone, tier, is_active, logo_url)
      `)
      .order('created_at', { ascending: false });
    if (status) query = query.eq('status', status);
    return await query;
  },

  getByVendorId: async (vendorId) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('vendor_subscriptions')
      .select('*')
      .eq('vendor_id', vendorId)
      .single();
  },

  create: async (vendorId, tier, billingPeriod = 'monthly', createdBy = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.rpc('create_vendor_subscription', {
      p_vendor_id: vendorId,
      p_tier: tier,
      p_billing_period: billingPeriod,
      p_created_by: createdBy
    });
  },

  activate: async (subscriptionId, paymentId = null, updatedBy = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.rpc('activate_subscription', {
      p_subscription_id: subscriptionId,
      p_payment_id: paymentId,
      p_updated_by: updatedBy
    });
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('vendor_subscriptions')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  suspend: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    // First get the vendor_id
    const { data: sub } = await supabase.from('vendor_subscriptions').select('vendor_id').eq('id', id).single();
    if (sub) {
      // Deactivate the service provider
      await supabase.from('service_providers').update({ is_active: false }).eq('id', sub.vendor_id);
    }
    return await supabase
      .from('vendor_subscriptions')
      .update({ status: 'suspended', updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  getUpcomingRenewals: async (days = 7) => {
    if (!supabase) return { data: [], error: null };
    return await supabase.rpc('get_upcoming_renewals', { p_days: days });
  },

  getGracePeriod: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase.rpc('get_grace_period_subscriptions');
  },

  getDashboardStats: async () => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('subscription_dashboard_stats').select('*').single();
  }
};

// ==================== PAYMENTS ====================
export const payments = {
  getAll: async (vendorId = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('payment_transactions')
      .select(`
        *,
        vendor:service_providers(id, name, email),
        recorded_by_user:profiles!payment_transactions_recorded_by_fkey(full_name, email)
      `)
      .order('payment_date', { ascending: false });
    if (vendorId) query = query.eq('vendor_id', vendorId);
    return await query;
  },

  getByVendorId: async (vendorId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('payment_transactions')
      .select('*')
      .eq('vendor_id', vendorId)
      .order('payment_date', { ascending: false });
  },

  record: async (vendorId, amount, paymentMethod, transactionRef = null, notes = null, recordedBy = null, activateSubscription = true) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.rpc('record_payment', {
      p_vendor_id: vendorId,
      p_amount: amount,
      p_payment_method: paymentMethod,
      p_transaction_ref: transactionRef,
      p_notes: notes,
      p_recorded_by: recordedBy,
      p_activate_subscription: activateSubscription
    });
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('payment_transactions').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('payment_transactions').update(data).eq('id', id).select().single();
  },

  getThisMonth: async () => {
    if (!supabase) return { data: [], error: null };
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    return await supabase
      .from('payment_transactions')
      .select('*')
      .gte('payment_date', startOfMonth.toISOString())
      .eq('status', 'completed');
  }
};

// ==================== INVOICES ====================
export const invoices = {
  getAll: async (vendorId = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('invoices')
      .select('*')
      .order('issued_date', { ascending: false });
    if (vendorId) query = query.eq('vendor_id', vendorId);
    return await query;
  },

  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('invoices').select('*').eq('id', id).single();
  },

  getByNumber: async (invoiceNumber) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('invoices').select('*').eq('invoice_number', invoiceNumber).single();
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    // Generate invoice number
    const { data: invNum } = await supabase.rpc('generate_invoice_number');
    return await supabase.from('invoices').insert({ ...data, invoice_number: invNum }).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('invoices').update(data).eq('id', id).select().single();
  }
};

// ==================== EMAIL TEMPLATES ====================
export const emailTemplates = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase.from('email_templates').select('*').order('name');
  },

  getByKey: async (templateKey) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('email_templates').select('*').eq('template_key', templateKey).single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('email_templates')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Helper to escape HTML entities to prevent XSS
  escapeHtml: (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  },

  // Render a template with variables (with XSS protection)
  render: (template, variables, escapeValues = true) => {
    let subject = template.subject;
    let bodyHtml = template.body_html;
    let bodyText = template.body_text;

    // Replace all {{variable}} placeholders with escaped values
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      // Escape HTML in values to prevent XSS attacks
      const safeValue = escapeValues ? emailTemplates.escapeHtml(value) : (value || '');
      subject = subject.replace(regex, safeValue);
      bodyHtml = bodyHtml.replace(regex, safeValue);
      if (bodyText) bodyText = bodyText.replace(regex, value || ''); // Plain text doesn't need escaping
    });

    return { subject, bodyHtml, bodyText };
  }
};

// ==================== NOTIFICATION LOG ====================
export const notificationLog = {
  getAll: async (vendorId = null, type = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('notification_log')
      .select('*')
      .order('sent_at', { ascending: false });
    if (vendorId) query = query.eq('vendor_id', vendorId);
    if (type) query = query.eq('notification_type', type);
    return await query;
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('notification_log').insert(data).select().single();
  }
};

// ==================== PAYMENT REQUESTS ====================
export const paymentRequests = {
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    try {
      let query = supabase
        .from('payment_requests')
        .select(`
          *,
          vendor:service_providers(id, name, email, phone, tier),
          reviewed_by_user:profiles!payment_requests_reviewed_by_fkey(full_name, email)
        `)
        .order('created_at', { ascending: false });
      if (status) query = query.eq('status', status);
      const result = await query;
      if (result.error) return { data: [], error: null };
      return result;
    } catch {
      return { data: [], error: null };
    }
  },

  getByVendorId: async (vendorId) => {
    if (!supabase) return { data: [], error: null };
    try {
      const result = await supabase
        .from('payment_requests')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });
      if (result.error) return { data: [], error: null };
      return result;
    } catch {
      return { data: [], error: null };
    }
  },

  getPending: async () => {
    if (!supabase) return { data: [], error: null };
    try {
      const result = await supabase
        .from('payment_requests')
        .select(`
          *,
          vendor:service_providers(id, name, email, phone, tier)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (result.error) return { data: [], error: null };
      return result;
    } catch {
      return { data: [], error: null };
    }
  },

  approve: async (requestId, adminNotes = null, reviewedBy = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    try {
      return await supabase.rpc('approve_payment_request', {
        p_request_id: requestId,
        p_admin_notes: adminNotes,
        p_reviewed_by: reviewedBy
      });
    } catch {
      return { error: { message: 'Unable to approve request' } };
    }
  },

  reject: async (requestId, rejectionReason, reviewedBy = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    try {
      return await supabase.rpc('reject_payment_request', {
        p_request_id: requestId,
        p_rejection_reason: rejectionReason,
        p_reviewed_by: reviewedBy
      });
    } catch {
      return { error: { message: 'Unable to reject request' } };
    }
  },

  // Generate access token for a vendor
  generateAccessToken: async (vendorId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    try {
      return await supabase.rpc('generate_vendor_access_token', {
        p_vendor_id: vendorId
      });
    } catch {
      return { error: { message: 'Unable to generate token' } };
    }
  }
};

// ==================== BANNER AD PACKAGES ====================
export const bannerAdPackages = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_packages')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
  },

  getByPlacement: async (placement) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_packages')
      .select('*')
      .eq('placement', placement)
      .eq('is_active', true)
      .order('duration_months', { ascending: true });
  },

  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('banner_ad_packages')
      .select('*')
      .eq('id', id)
      .single();
  }
};

// ==================== BANNER AD REQUESTS ====================
export const bannerAdRequests = {
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('banner_ad_requests')
      .select('*')
      .order('created_at', { ascending: false });
    if (status) query = query.eq('status', status);
    return await query;
  },

  getPending: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_requests')
      .select('*')
      .in('status', ['pending', 'payment_submitted', 'design_pending', 'design_review'])
      .order('created_at', { ascending: false });
  },

  getByEmail: async (email) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_requests')
      .select('*')
      .eq('contact_email', email)
      .order('created_at', { ascending: false });
  },

  getByServiceProvider: async (providerId, providerType) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('banner_ad_requests')
      .select('*')
      .eq('service_provider_id', providerId)
      .eq('service_provider_type', providerType)
      .order('created_at', { ascending: false });
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id).select().single();
  },

  // Admin actions
  approveForPayment: async (id, adminNotes = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'awaiting_payment',
      admin_notes: adminNotes,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  verifyPayment: async (id, verifiedBy, adminNotes = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'payment_verified',
      payment_verified_by: verifiedBy,
      payment_verified_at: new Date().toISOString(),
      admin_notes: adminNotes,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  activate: async (id, bannerAdId, startDate, endDate) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'active',
      banner_ad_id: bannerAdId,
      actual_start_date: startDate,
      actual_end_date: endDate,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  reject: async (id, rejectionReason) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'rejected',
      rejection_reason: rejectionReason,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  // Vendor actions
  submitPaymentProof: async (id, paymentMethod, paymentReference, paymentProofUrl) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'payment_submitted',
      payment_method: paymentMethod,
      payment_reference: paymentReference,
      payment_proof_url: paymentProofUrl,
      payment_date: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  },

  submitAdCreative: async (id, imageUrl, imageMobileUrl, linkUrl, altText) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('banner_ad_requests').update({
      status: 'design_review',
      ad_image_url: imageUrl,
      ad_image_url_mobile: imageMobileUrl,
      ad_link_url: linkUrl,
      ad_alt_text: altText,
      updated_at: new Date().toISOString()
    }).eq('id', id).select().single();
  }
};

// ==================== HERO CAROUSEL ====================
export const heroCarousel = {
  // Get carousel settings
  getSettings: async () => {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('hero_carousel_settings')
      .select('*')
      .limit(1)
      .single();
    return { data, error };
  },

  // Update carousel settings
  updateSettings: async (settings) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    // Get the first (and only) settings row ID
    const { data: existing } = await supabase
      .from('hero_carousel_settings')
      .select('id')
      .limit(1)
      .single();

    if (existing?.id) {
      return await supabase
        .from('hero_carousel_settings')
        .update({ ...settings, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
        .select()
        .single();
    }
    // If no settings exist, create them
    return await supabase
      .from('hero_carousel_settings')
      .insert(settings)
      .select()
      .single();
  },

  // Get all slides (admin)
  getAllSlides: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('hero_slides')
      .select('*')
      .order('sort_order', { ascending: true });
  },

  // Get active slides (public)
  getActiveSlides: async () => {
    if (!supabase) return { data: [], error: null };
    const now = new Date().toISOString();
    return await supabase
      .from('hero_slides')
      .select('*')
      .eq('is_active', true)
      .or(`start_date.is.null,start_date.lte.${now}`)
      .or(`end_date.is.null,end_date.gte.${now}`)
      .order('sort_order', { ascending: true });
  },

  // Get slide by ID
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('hero_slides')
      .select('*')
      .eq('id', id)
      .single();
  },

  // Create slide
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('hero_slides')
      .insert(data)
      .select()
      .single();
  },

  // Update slide
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('hero_slides')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Delete slide
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('hero_slides')
      .delete()
      .eq('id', id);
  },

  // Reorder slides
  reorder: async (slideIds) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const updates = slideIds.map((id, index) =>
      supabase
        .from('hero_slides')
        .update({ sort_order: index })
        .eq('id', id)
    );
    await Promise.all(updates);
    return { error: null };
  },

  // Toggle slide active status
  toggleActive: async (id, isActive) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('hero_slides')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  }
};

// Contact form submissions
export const contactSubmissions = {
  // Submit a contact form
  submit: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const result = await supabase
      .from('contact_submissions')
      .insert({
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        subject: data.subject,
        category: data.category || 'general',
        message: data.message,
        status: 'new',
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    // Create admin notification for new contact submission
    if (!result.error && result.data) {
      const notificationPayload = {
        type: 'contact_submission',
        title: `New Contact: ${data.subject}`,
        message: `${data.name} sent a message about ${data.category || 'general inquiry'}`,
        data: {
          submission_id: result.data.id,
          name: data.name,
          email: data.email,
          phone: data.phone || null,
          subject: data.subject,
          category: data.category || 'general',
          message: data.message
        }
      };
      // Write DB record (shows in admin panel)
      adminNotifications.create(notificationPayload).catch(err => {
        logger.warn('Failed to create contact notification record:', err?.message);
      });
      // Also fire email directly — don't rely solely on realtime subscription
      adminNotifications.sendEmail(notificationPayload).catch(err => {
        logger.warn('Failed to send contact notification email:', err?.message);
      });
    }

    return result;
  },

  // Admin: Get all submissions
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('contact_submissions')
      .select('*')
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }
    return await query;
  },

  // Admin: Update submission status
  updateStatus: async (id, status, adminNotes = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const updates = {
      status,
      updated_at: new Date().toISOString()
    };
    if (adminNotes !== null) updates.admin_notes = adminNotes;
    return await supabase
      .from('contact_submissions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
  },

  // Admin: Delete submission
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('contact_submissions').delete().eq('id', id);
  },

  // Get count of new submissions (for admin badge)
  getNewCount: async () => {
    if (!supabase) return { count: 0, error: null };
    const { count, error } = await supabase
      .from('contact_submissions')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'new');
    return { count: count || 0, error };
  }
};

// ==================== EDUCATION: SCHOOLS ====================
export const schools = {
  // Get all active schools with optional filters
  getAll: async (filters = {}) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('schools')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (filters.parish) query = query.eq('parish', filters.parish);
    if (filters.level) query = query.eq('level', filters.level);
    if (filters.type) query = query.eq('type', filters.type);
    if (filters.ownership) query = query.eq('ownership', filters.ownership);
    if (filters.gender) query = query.eq('gender', filters.gender);
    if (filters.has_boarding) query = query.eq('has_boarding', true);
    if (filters.sen_support) query = query.eq('sen_support', true);
    if (filters.limit) query = query.limit(filters.limit);

    return await query;
  },

  // Get all schools for admin (including inactive)
  getAllAdmin: async (filters = {}) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('schools')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters.parish) query = query.eq('parish', filters.parish);
    if (filters.level) query = query.eq('level', filters.level);
    if (filters.is_active !== undefined) query = query.eq('is_active', filters.is_active);

    return await query;
  },

  // Get school by ID with related data
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('schools')
      .select('*')
      .eq('id', id)
      .single();
  },

  // Get school by slug
  getBySlug: async (slug) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('schools')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .single();
  },

  // Create school
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('schools').insert(data).select().single();
  },

  // Update school
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('schools')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Delete school
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('schools').delete().eq('id', id);
  },

  // Search schools by name
  search: async (searchTerm, limit = 20) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('schools')
      .select('id, name, parish, level, type')
      .eq('is_active', true)
      .ilike('name', `%${searchTerm}%`)
      .limit(limit);
  },

  // Get schools count by parish
  getCountByParish: async () => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('schools')
      .select('parish')
      .eq('is_active', true);

    if (error) return { data: [], error };

    const counts = {};
    (data || []).forEach(s => {
      counts[s.parish] = (counts[s.parish] || 0) + 1;
    });

    return { data: Object.entries(counts).map(([parish, count]) => ({ parish, count })), error: null };
  },

  // Get dashboard stats
  getStats: async () => {
    if (!supabase) return { data: null, error: null };
    const [total, primary, secondary, tertiary] = await Promise.all([
      supabase.from('schools').select('id', { count: 'exact', head: true }).eq('is_active', true),
      supabase.from('schools').select('id', { count: 'exact', head: true }).eq('is_active', true).eq('level', 'primary'),
      supabase.from('schools').select('id', { count: 'exact', head: true }).eq('is_active', true).eq('level', 'secondary'),
      supabase.from('schools').select('id', { count: 'exact', head: true }).eq('is_active', true).eq('level', 'tertiary')
    ]);

    return {
      data: {
        total: total.count || 0,
        primary: primary.count || 0,
        secondary: secondary.count || 0,
        tertiary: tertiary.count || 0
      },
      error: null
    };
  }
};

// ==================== EDUCATION: EXAM RESULTS ====================
export const examResults = {
  // Get exam results for a school
  getBySchool: async (schoolId, year = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('exam_results')
      .select('*')
      .eq('school_id', schoolId)
      .order('year', { ascending: false });

    if (year) query = query.eq('year', year);
    return await query;
  },

  // Get all results for a specific exam type and year
  getByExamTypeYear: async (examType, year) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('exam_results')
      .select('*, school:schools(name, parish)')
      .eq('exam_type', examType)
      .eq('year', year)
      .order('pass_rate', { ascending: false });
  },

  // Create exam result
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_results').insert(data).select().single();
  },

  // Bulk create exam results
  bulkCreate: async (results) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_results').insert(results);
  },

  // Update exam result
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('exam_results')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Delete exam result
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_results').delete().eq('id', id);
  }
};

// ==================== EDUCATION: EXTRACURRICULAR RESULTS ====================
export const extracurricularResults = {
  // Get results for a school
  getBySchool: async (schoolId, category = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('extracurricular_results')
      .select('*')
      .eq('school_id', schoolId)
      .order('year', { ascending: false });

    if (category) query = query.eq('category', category);
    return await query;
  },

  // Get top performers by category
  getTopByCategory: async (category, year, limit = 10) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('extracurricular_results')
      .select('*, school:schools(name, parish)')
      .eq('category', category)
      .eq('year', year)
      .order('placement', { ascending: true })
      .limit(limit);
  },

  // Create result
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('extracurricular_results').insert(data).select().single();
  },

  // Update result
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('extracurricular_results').update(data).eq('id', id).select().single();
  },

  // Delete result
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('extracurricular_results').delete().eq('id', id);
  }
};

// ==================== EDUCATION: RANKINGS ====================
export const schoolRankings = {
  // Get rankings by type and year
  getByTypeYear: async (rankingType, year) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_rankings')
      .select('*, school:schools(name, parish, level, type)')
      .eq('ranking_type', rankingType)
      .eq('year', year)
      .order('rank', { ascending: true });
  },

  // Get school's ranking history
  getBySchool: async (schoolId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_rankings')
      .select('*')
      .eq('school_id', schoolId)
      .order('year', { ascending: false });
  },

  // Upsert ranking (for computed rankings)
  upsert: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_rankings')
      .upsert(data, { onConflict: 'school_id,ranking_type,year' })
      .select()
      .single();
  },

  // Bulk upsert rankings
  bulkUpsert: async (rankings) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_rankings')
      .upsert(rankings, { onConflict: 'school_id,ranking_type,year' });
  }
};

// ==================== EDUCATION: SCHOLARSHIPS ====================
export const scholarships = {
  // Get all active scholarships
  getAll: async (filters = {}) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('scholarships')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (filters.provider_type) query = query.eq('provider_type', filters.provider_type);
    if (filters.level) query = query.contains('levels', [filters.level]);

    return await query;
  },

  // Get scholarship by ID
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('scholarships').select('*').eq('id', id).single();
  },

  // Create scholarship
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('scholarships').insert(data).select().single();
  },

  // Update scholarship
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('scholarships')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Delete scholarship
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('scholarships').delete().eq('id', id);
  }
};

// ==================== EDUCATION: DISRUPTION EVENTS ====================
export const disruptionEvents = {
  // Get active events
  getActive: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('disruption_events')
      .select('*')
      .eq('is_active', true)
      .order('start_date', { ascending: false });
  },

  // Get all events
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('disruption_events')
      .select('*')
      .order('start_date', { ascending: false });
  },

  // Get event by ID with affected schools
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('disruption_events')
      .select('*, school_disruptions(*, school:schools(name, parish))')
      .eq('id', id)
      .single();
  },

  // Create event
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('disruption_events').insert(data).select().single();
  },

  // Update event
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('disruption_events')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Toggle active status
  toggleActive: async (id, isActive) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('disruption_events')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  }
};

// ==================== EDUCATION: SCHOOL DISRUPTIONS ====================
export const schoolDisruptions = {
  // Get disruptions for a school
  getBySchool: async (schoolId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_disruptions')
      .select('*, event:disruption_events(*)')
      .eq('school_id', schoolId)
      .order('last_updated', { ascending: false });
  },

  // Get disruptions for an event
  getByEvent: async (eventId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_disruptions')
      .select('*, school:schools(name, parish)')
      .eq('event_id', eventId)
      .order('last_updated', { ascending: false });
  },

  // Create/update school disruption
  upsert: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_disruptions')
      .upsert({ ...data, last_updated: new Date().toISOString() }, { onConflict: 'school_id,event_id' })
      .select()
      .single();
  },

  // Bulk upsert
  bulkUpsert: async (disruptions) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const withTimestamp = disruptions.map(d => ({ ...d, last_updated: new Date().toISOString() }));
    return await supabase
      .from('school_disruptions')
      .upsert(withTimestamp, { onConflict: 'school_id,event_id' });
  }
};

// ==================== EDUCATION: EMERGENCY ALERTS ====================
export const emergencyAlerts = {
  // Get active alerts
  getActive: async () => {
    if (!supabase) return { data: [], error: null };
    const now = new Date().toISOString();
    return await supabase
      .from('emergency_alerts')
      .select('*')
      .eq('is_active', true)
      .lte('starts_at', now)
      .or(`expires_at.is.null,expires_at.gte.${now}`)
      .order('severity', { ascending: false });
  },

  // Get all alerts (admin)
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('emergency_alerts')
      .select('*')
      .order('created_at', { ascending: false });
  },

  // Create alert
  create: async (data, userId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('emergency_alerts')
      .insert({ ...data, created_by: userId })
      .select()
      .single();
  },

  // Update alert
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('emergency_alerts')
      .update(data)
      .eq('id', id)
      .select()
      .single();
  },

  // Delete alert
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('emergency_alerts').delete().eq('id', id);
  },

  // Toggle active
  toggleActive: async (id, isActive) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('emergency_alerts')
      .update({ is_active: isActive })
      .eq('id', id)
      .select()
      .single();
  }
};

// ==================== EDUCATION: JOBS ====================
export const educationJobs = {
  // Get active jobs
  getAll: async (filters = {}) => {
    if (!supabase) return { data: [], error: null };
    const now = new Date().toISOString();
    let query = supabase
      .from('education_jobs')
      .select('*, school:schools(name, parish)')
      .eq('is_active', true)
      // Belt as well as braces: the RLS policy already hides anything not
      // approved, but a misapplied migration should not silently publish the
      // moderation queue to the public board.
      .eq('status', 'approved')
      .or(`expires_at.is.null,expires_at.gte.${now}`)
      .order('created_at', { ascending: false });

    if (filters.parish) query = query.eq('parish', filters.parish);
    if (filters.role_type) query = query.eq('role_type', filters.role_type);
    if (filters.job_type) query = query.eq('job_type', filters.job_type);

    return await query;
  },

  // Get job by ID
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('education_jobs')
      .select('*, school:schools(name, parish, email, phone)')
      .eq('id', id)
      .single();
  },

  // Create job.
  //
  // posted_by and status are stamped by a BEFORE INSERT trigger
  // (migration-education-job-postings.sql) rather than trusted from here — the
  // client can send whatever it likes, so the database is the thing that
  // decides who owns a listing and that it starts unpublished.
  create: async (data, userId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('education_jobs')
      .insert({ ...data, posted_by: userId })
      .select()
      .single();
  },

  // Everything the signed-in user has posted, at any status, newest first.
  getMine: async (userId) => {
    if (!supabase || !userId) return { data: [], error: null };
    return await supabase
      .from('education_jobs')
      .select('*')
      .eq('posted_by', userId)
      .order('created_at', { ascending: false });
  },

  // ---- moderation (admin) ----
  // Deliberately not filtered by is_active: a listing switched off after
  // rejection still needs to be reachable in the queue.
  getForReview: async (status = 'pending') => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('education_jobs')
      .select('*')
      .order('created_at', { ascending: false });
    if (status !== 'all') query = query.eq('status', status);
    return await query;
  },

  moderate: async (id, status, adminId, note = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('education_jobs')
      .update({
        status,
        moderation_note: note,
        reviewed_by: adminId,
        reviewed_at: new Date().toISOString(),
        // A rejected listing stays off the board even if it is later approved
        // by mistake — approving is what turns it back on.
        is_active: status === 'approved',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
  },

  countPending: async () => {
    if (!supabase) return 0;
    const { count } = await supabase
      .from('education_jobs')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'pending');
    return count || 0;
  },

  // Update job
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('education_jobs')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Delete job
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('education_jobs').delete().eq('id', id);
  }
};

// ==================== EDUCATION: DATA SUGGESTIONS ====================
export const schoolDataSuggestions = {
  // Submit a suggestion
  submit: async (data, userId = null) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const result = await supabase
      .from('school_data_suggestions')
      .insert({ ...data, submitted_by: userId })
      .select()
      .single();

    // Notify admins of the new data suggestion (best-effort; reuses the data_correction type)
    if (!result.error) {
      const notif = {
        type: 'data_correction',
        title: `New Data Suggestion: ${data.school_name || 'school data'}`,
        message: `${data.field_name ? `Field "${data.field_name}"` : 'A correction'} was suggested${data.school_name ? ` for ${data.school_name}` : ''}`,
        data: {
          suggestion_id: result.data?.id,
          school_name: data.school_name,
          school_id: data.school_id,
          field_name: data.field_name,
          current_value: data.current_value,
          suggested_value: data.suggested_value,
          reason: data.reason,
        }
      };
      adminNotifications.create(notif).catch(() => {});
      adminNotifications.sendEmail(notif).catch(() => {});
    }

    return result;
  },

  // Get all suggestions (admin)
  getAll: async (status = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('school_data_suggestions')
      .select('*, school:schools(name, parish)')
      .order('created_at', { ascending: false });

    if (status) query = query.eq('status', status);
    return await query;
  },

  // Approve suggestion
  approve: async (id, reviewerId) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_data_suggestions')
      .update({
        status: 'approved',
        reviewed_by: reviewerId,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
  },

  // Reject suggestion
  reject: async (id, reviewerId, reason) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_data_suggestions')
      .update({
        status: 'rejected',
        reviewed_by: reviewerId,
        reviewed_at: new Date().toISOString(),
        rejection_reason: reason
      })
      .eq('id', id)
      .select()
      .single();
  },

  // Get pending count
  getPendingCount: async () => {
    if (!supabase) return { count: 0, error: null };
    const { count, error } = await supabase
      .from('school_data_suggestions')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');
    return { count: count || 0, error };
  }
};

// ==================== EDUCATION: CAREER PATHS ====================
export const careerPaths = {
  // Get all career paths
  getAll: async (category = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('career_paths')
      .select('*')
      .order('name');

    if (category) query = query.eq('category', category);
    return await query;
  },

  // Get career path by ID
  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('career_paths').select('*').eq('id', id).single();
  },

  // Create career path
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('career_paths').insert(data).select().single();
  },

  // Update career path
  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('career_paths')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
  },

  // Delete career path
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('career_paths').delete().eq('id', id);
  }
};

// ==================== EDUCATION: EXAM SUBJECTS ====================
export const examSubjects = {
  // Get all subjects for an exam type
  getByExamType: async (examType) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('exam_subjects')
      .select('*')
      .eq('exam_type', examType)
      .order('subject_name');
  },

  // Get all subjects
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('exam_subjects')
      .select('*')
      .order('exam_type')
      .order('subject_name');
  },

  // Create subject
  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_subjects').insert(data).select().single();
  },

  // Bulk create subjects
  bulkCreate: async (subjects) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_subjects').insert(subjects);
  },

  // Delete subject
  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_subjects').delete().eq('id', id);
  }
};

// ==================== STUDENT ENGAGEMENT: SIDE HUSTLES ====================
export const sideHustles = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('side_hustles')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
  },

  getAllAdmin: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('side_hustles')
      .select('*')
      .order('display_order');
  },

  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('side_hustles').select('*').eq('id', id).single();
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('side_hustles').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('side_hustles').update(data).eq('id', id).select().single();
  },

  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('side_hustles').delete().eq('id', id);
  },

  toggleActive: async (id, isActive) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('side_hustles').update({ is_active: isActive }).eq('id', id).select().single();
  }
};

// ==================== STUDENT ENGAGEMENT: SUMMER JOBS ====================
export const summerJobs = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('summer_jobs')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
  },

  getByType: async (jobType) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('summer_jobs')
      .select('*')
      .eq('job_type', jobType)
      .eq('is_active', true)
      .order('display_order');
  },

  getAllAdmin: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('summer_jobs')
      .select('*')
      .order('job_type')
      .order('display_order');
  },

  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('summer_jobs').select('*').eq('id', id).single();
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('summer_jobs').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('summer_jobs').update(data).eq('id', id).select().single();
  },

  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('summer_jobs').delete().eq('id', id);
  },

  toggleActive: async (id, isActive) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('summer_jobs').update({ is_active: isActive }).eq('id', id).select().single();
  }
};

// ==================== STUDENT ENGAGEMENT: EXTRACURRICULAR ACTIVITIES ====================
export const extracurriculars = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('extracurricular_activities')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
  },

  getByCategory: async (category) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('extracurricular_activities')
      .select('*')
      .eq('category', category)
      .eq('is_active', true)
      .order('display_order');
  },

  getByType: async (activityType) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('extracurricular_activities')
      .select('*')
      .eq('activity_type', activityType)
      .eq('is_active', true)
      .order('category')
      .order('display_order');
  },

  getAllAdmin: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('extracurricular_activities')
      .select('*')
      .order('activity_type')
      .order('category')
      .order('display_order');
  },

  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('extracurricular_activities').select('*').eq('id', id).single();
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('extracurricular_activities').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('extracurricular_activities').update(data).eq('id', id).select().single();
  },

  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('extracurricular_activities').delete().eq('id', id);
  },

  toggleActive: async (id, isActive) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('extracurricular_activities').update({ is_active: isActive }).eq('id', id).select().single();
  }
};

// ==================== STUDENT ENGAGEMENT: INTEREST CATEGORIES (Opportunity Matcher) ====================
export const interestCategories = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('interest_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
  },

  getAllAdmin: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('interest_categories')
      .select('*')
      .order('display_order');
  },

  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('interest_categories').select('*').eq('id', id).single();
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('interest_categories').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('interest_categories').update(data).eq('id', id).select().single();
  },

  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('interest_categories').delete().eq('id', id);
  },

  toggleActive: async (id, isActive) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('interest_categories').update({ is_active: isActive }).eq('id', id).select().single();
  }
};

// ==================== STUDENT ENGAGEMENT: EXAM CALENDAR ====================
export const examCalendar = {
  getAll: async (year = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('exam_calendar')
      .select('*')
      .eq('is_active', true)
      .order('event_date');
    if (year) query = query.eq('year', year);
    return await query;
  },

  getByExamType: async (examType, year = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('exam_calendar')
      .select('*')
      .eq('exam_type', examType)
      .eq('is_active', true)
      .order('event_date');
    if (year) query = query.eq('year', year);
    return await query;
  },

  getUpcoming: async (days = 90) => {
    if (!supabase) return { data: [], error: null };
    const today = new Date().toISOString().split('T')[0];
    const futureDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return await supabase
      .from('exam_calendar')
      .select('*')
      .eq('is_active', true)
      .gte('event_date', today)
      .lte('event_date', futureDate)
      .order('event_date');
  },

  getAllAdmin: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('exam_calendar')
      .select('*')
      .order('year', { ascending: false })
      .order('event_date');
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_calendar').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_calendar').update(data).eq('id', id).select().single();
  },

  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_calendar').delete().eq('id', id);
  }
};

// ==================== STUDENT ENGAGEMENT: EXAM FEES ====================
export const examFees = {
  getAll: async (year = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('exam_fees')
      .select('*')
      .eq('is_active', true)
      .order('exam_type')
      .order('fee_type');
    if (year) query = query.eq('year', year);
    return await query;
  },

  getByExamType: async (examType, year = null) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('exam_fees')
      .select('*')
      .eq('exam_type', examType)
      .eq('is_active', true);
    if (year) query = query.eq('year', year);
    return await query;
  },

  getAllAdmin: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('exam_fees')
      .select('*')
      .order('year', { ascending: false })
      .order('exam_type');
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_fees').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_fees').update(data).eq('id', id).select().single();
  },

  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_fees').delete().eq('id', id);
  }
};

// ==================== STUDENT ENGAGEMENT: STUDY METHODS ====================
export const studyMethods = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('study_methods')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
  },

  getAllAdmin: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('study_methods')
      .select('*')
      .order('display_order');
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('study_methods').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('study_methods').update(data).eq('id', id).select().single();
  },

  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('study_methods').delete().eq('id', id);
  }
};

// ==================== STUDENT ENGAGEMENT: EXAM SUBJECTS (Enhanced) ====================
export const examSubjectsEnhanced = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('exam_subjects')
      .select('*')
      .eq('is_active', true)
      .order('exam_type')
      .order('display_order');
  },

  getByExamType: async (examType) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('exam_subjects')
      .select('*')
      .eq('exam_type', examType)
      .eq('is_active', true)
      .order('display_order');
  },

  getAllAdmin: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('exam_subjects')
      .select('*')
      .order('exam_type')
      .order('display_order');
  },

  getById: async (id) => {
    if (!supabase) return { data: null, error: null };
    return await supabase.from('exam_subjects').select('*').eq('id', id).single();
  },

  create: async (data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_subjects').insert(data).select().single();
  },

  update: async (id, data) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_subjects').update(data).eq('id', id).select().single();
  },

  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_subjects').delete().eq('id', id);
  },

  toggleActive: async (id, isActive) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase.from('exam_subjects').update({ is_active: isActive }).eq('id', id).select().single();
  }
};

// ==================== COMMUNITY RANKINGS & SENTIMENT ====================

// School Votes (for Hot Schools ranking)
export const schoolVotes = {
  getVoteCounts: async () => {
    if (!supabase) return { data: [], error: null };
    // Get vote counts grouped by school
    return await supabase
      .from('school_votes')
      .select('school_id, school_name')
      .order('created_at', { ascending: false });
  },

  getTopVoted: async (limit = 20) => {
    if (!supabase) return { data: [], error: null };
    // Use RPC function or manual aggregation
    const { data, error } = await supabase
      .from('school_votes')
      .select('school_id, school_name');

    if (error) return { data: [], error };

    // Aggregate votes client-side
    const voteCounts = {};
    (data || []).forEach(vote => {
      if (!voteCounts[vote.school_id]) {
        voteCounts[vote.school_id] = { school_id: vote.school_id, school_name: vote.school_name, votes: 0 };
      }
      voteCounts[vote.school_id].votes++;
    });

    const sorted = Object.values(voteCounts)
      .sort((a, b) => b.votes - a.votes)
      .slice(0, limit);

    return { data: sorted, error: null };
  },

  castVote: async (schoolId, schoolName, fingerprint) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_votes')
      .insert({ school_id: schoolId, school_name: schoolName, voter_fingerprint: fingerprint })
      .select()
      .single();
  },

  hasVoted: async (schoolId, fingerprint) => {
    if (!supabase) return { data: false, error: null };
    const { data, error } = await supabase
      .from('school_votes')
      .select('id')
      .eq('school_id', schoolId)
      .eq('voter_fingerprint', fingerprint)
      .single();
    return { data: !!data, error };
  },

  removeVote: async (schoolId, fingerprint) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_votes')
      .delete()
      .eq('school_id', schoolId)
      .eq('voter_fingerprint', fingerprint);
  }
};

// School Sentiment (reviews and ratings)
export const schoolSentiment = {
  getForSchool: async (schoolId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_sentiment')
      .select('*')
      .eq('school_id', schoolId)
      .eq('is_approved', true)
      .order('created_at', { ascending: false });
  },

  getAverages: async (schoolId) => {
    if (!supabase) return { data: null, error: null };
    const { data, error } = await supabase
      .from('school_sentiment')
      .select('overall_rating, academics_rating, facilities_rating, safety_rating, extracurricular_rating, teacher_quality_rating')
      .eq('school_id', schoolId)
      .eq('is_approved', true);

    if (error || !data || data.length === 0) return { data: null, error };

    const avg = (arr, field) => {
      const valid = arr.filter(r => r[field] != null);
      return valid.length ? (valid.reduce((sum, r) => sum + r[field], 0) / valid.length).toFixed(1) : null;
    };

    return {
      data: {
        overall: avg(data, 'overall_rating'),
        academics: avg(data, 'academics_rating'),
        facilities: avg(data, 'facilities_rating'),
        safety: avg(data, 'safety_rating'),
        extracurricular: avg(data, 'extracurricular_rating'),
        teachers: avg(data, 'teacher_quality_rating'),
        reviewCount: data.length
      },
      error: null
    };
  },

  submit: async (sentimentData) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const result = await supabase
      .from('school_sentiment')
      .insert(sentimentData)
      .select()
      .single();

    // Notify admins of the new review (best-effort)
    if (!result.error) {
      const school = sentimentData.school_name || sentimentData.school_id;
      const notif = {
        type: 'sentiment_submission',
        title: `New School Review: ${school}`,
        message: `${sentimentData.user_type || 'Someone'} reviewed ${school}${sentimentData.overall_rating ? ` — ${sentimentData.overall_rating}/5` : ''}`,
        data: {
          review_id: result.data?.id,
          school_name: sentimentData.school_name,
          school_id: sentimentData.school_id,
          user_type: sentimentData.user_type,
          overall_rating: sentimentData.overall_rating,
          pros: sentimentData.pros,
          cons: sentimentData.cons,
          advice: sentimentData.advice,
        }
      };
      adminNotifications.create(notif).catch(() => {});
      adminNotifications.sendEmail(notif).catch(() => {});
    }

    return result;
  },

  getAllAdmin: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_sentiment')
      .select('*')
      .order('created_at', { ascending: false });
  },

  approve: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_sentiment')
      .update({ is_approved: true })
      .eq('id', id)
      .select()
      .single();
  },

  reject: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_sentiment')
      .update({ is_approved: false })
      .eq('id', id)
      .select()
      .single();
  },

  getPending: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_sentiment')
      .select('*')
      .eq('is_approved', false)
      .order('created_at', { ascending: false });
  },

  delete: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_sentiment')
      .delete()
      .eq('id', id);
  }
};

// Data Suggestions (community corrections)
export const dataSuggestions = {
  submit: async (suggestionData) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const result = await supabase
      .from('school_data_suggestions')
      .insert(suggestionData)
      .select()
      .single();

    // Notify admins of the new data suggestion (best-effort; reuses the data_correction type)
    if (!result.error) {
      const notif = {
        type: 'data_correction',
        title: `New Data Suggestion: ${suggestionData.school_name || 'school data'}`,
        message: `${suggestionData.field_name ? `Field "${suggestionData.field_name}"` : 'A correction'} was suggested${suggestionData.school_name ? ` for ${suggestionData.school_name}` : ''}`,
        data: {
          suggestion_id: result.data?.id,
          school_name: suggestionData.school_name,
          school_id: suggestionData.school_id,
          field_name: suggestionData.field_name,
          current_value: suggestionData.current_value,
          suggested_value: suggestionData.suggested_value,
          reason: suggestionData.reason,
        }
      };
      adminNotifications.create(notif).catch(() => {});
      adminNotifications.sendEmail(notif).catch(() => {});
    }

    return result;
  },

  getPending: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_data_suggestions')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
  },

  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_data_suggestions')
      .select('*')
      .order('created_at', { ascending: false });
  },

  approve: async (id, reviewedBy) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_data_suggestions')
      .update({
        status: 'approved',
        reviewed_by: reviewedBy,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
  },

  reject: async (id, reviewedBy, reason) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_data_suggestions')
      .update({
        status: 'rejected',
        reviewed_by: reviewedBy,
        reviewed_at: new Date().toISOString(),
        review_notes: reason
      })
      .eq('id', id)
      .select()
      .single();
  }
};

// Extracurricular Achievements
export const extracurricularAchievements = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_extracurricular_achievements')
      .select('*')
      .eq('verified', true)
      .order('year', { ascending: false })
      .order('points', { ascending: false });
  },

  getBySchool: async (schoolId) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_extracurricular_achievements')
      .select('*')
      .eq('school_id', schoolId)
      .eq('verified', true)
      .order('year', { ascending: false });
  },

  getTopSchools: async (year = null, limit = 20) => {
    if (!supabase) return { data: [], error: null };
    let query = supabase
      .from('school_extracurricular_achievements')
      .select('school_id, school_name, points')
      .eq('verified', true);

    if (year) {
      query = query.eq('year', year);
    }

    const { data, error } = await query;
    if (error) return { data: [], error };

    // Aggregate points by school
    const schoolPoints = {};
    (data || []).forEach(a => {
      if (!schoolPoints[a.school_id]) {
        schoolPoints[a.school_id] = { school_id: a.school_id, school_name: a.school_name, total_points: 0, achievement_count: 0 };
      }
      schoolPoints[a.school_id].total_points += a.points || 0;
      schoolPoints[a.school_id].achievement_count++;
    });

    const sorted = Object.values(schoolPoints)
      .sort((a, b) => b.total_points - a.total_points)
      .slice(0, limit)
      .map((s, i) => ({ ...s, rank: i + 1 }));

    return { data: sorted, error: null };
  },

  submit: async (achievementData) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    const result = await supabase
      .from('school_extracurricular_achievements')
      .insert({ ...achievementData, verified: false })
      .select()
      .single();

    // Notify admins so the submission can be reviewed/verified (best-effort)
    if (!result.error) {
      const notif = {
        type: 'achievement_submission',
        title: `New Achievement: ${achievementData.school_name}`,
        message: `${achievementData.school_name} — ${achievementData.achievement_name || 'achievement'} (${achievementData.category})`,
        data: {
          submission_id: result.data?.id,
          school_name: achievementData.school_name,
          category: achievementData.category,
          achievement_name: achievementData.achievement_name,
          competition_name: achievementData.competition_name,
          level: achievementData.level,
          placement: achievementData.placement,
          year: achievementData.year,
          source_url: achievementData.source_url,
        }
      };
      adminNotifications.create(notif).catch(() => {});
      adminNotifications.sendEmail(notif).catch(() => {});
    }

    return result;
  },

  verify: async (id) => {
    if (!supabase) return { error: { message: 'Supabase not configured' } };
    return await supabase
      .from('school_extracurricular_achievements')
      .update({ verified: true })
      .eq('id', id)
      .select()
      .single();
  }
};

// Environment Scores
export const environmentScores = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('school_environment_scores')
      .select('*')
      .order('facilities_score', { ascending: false });
  },

  getBySchool: async (schoolId) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('school_environment_scores')
      .select('*')
      .eq('school_id', schoolId)
      .single();
  },

  getTopSchools: async (limit = 20) => {
    if (!supabase) return { data: [], error: null };
    const { data, error } = await supabase
      .from('school_environment_scores')
      .select('*');

    if (error) return { data: [], error };

    // Calculate composite environment score
    const scored = (data || []).map(s => {
      const composite = (
        (s.facilities_score || 0) * 0.25 +
        (s.library_quality || 0) * 0.15 +
        (s.lab_equipment || 0) * 0.15 +
        (s.sports_facilities || 0) * 0.1 +
        (s.it_resources || 0) * 0.1 +
        (s.sen_support_score || 0) * 0.1 +
        (s.safety_score || 0) * 0.15
      );
      return { ...s, composite_score: composite };
    });

    const sorted = scored
      .sort((a, b) => b.composite_score - a.composite_score)
      .slice(0, limit)
      .map((s, i) => ({ ...s, rank: i + 1 }));

    return { data: sorted, error: null };
  }
};

// Computed Rankings (pre-aggregated for performance)
export const computedRankings = {
  getAll: async () => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('computed_rankings')
      .select('*')
      .order('overall_rank');
  },

  getByType: async (rankingType) => {
    if (!supabase) return { data: [], error: null };
    const orderCol = `${rankingType}_rank`;
    return await supabase
      .from('computed_rankings')
      .select('*')
      .not(orderCol, 'is', null)
      .order(orderCol);
  },

  getBySchool: async (schoolId) => {
    if (!supabase) return { data: null, error: null };
    return await supabase
      .from('computed_rankings')
      .select('*')
      .eq('school_id', schoolId)
      .single();
  },

  getTrending: async (limit = 10) => {
    if (!supabase) return { data: [], error: null };
    return await supabase
      .from('computed_rankings')
      .select('*')
      .eq('is_trending', true)
      .order('popularity_rank')
      .limit(limit);
  }
};

// ---- Attach education data helpers to the unified `db` object ----
// Admin pages (AdminEducation) reference these as db.schools / db.examResults, but
// they are defined above as standalone exports. Wire them onto `db` so those calls
// resolve instead of throwing (which previously left the admin pages showing no data).
db.schools = schools;
db.examResults = examResults;
db.disruptionEvents = disruptionEvents;
db.schoolDataSuggestions = schoolDataSuggestions;
db.dataSuggestions = dataSuggestions;

export default supabase;
